/* ============================================================================
   Deal Basket — Shopify theme JS
   Ports the interactive behaviour of the React app (ThemeContext, CartContext,
   ProductCard, ProductDetail, Navbar, sonner toasts, framer-motion reveals)
   to vanilla JS on top of Shopify's AJAX Cart API.

   Events used across the theme (all bubble from the element named):
     db:variant-change  on #product-form   detail: { variant }        (variant picked)
     db:stepper         on #product-form   detail: { shown: bool }    (qty stepper shown/hidden)
     db:cart-updated    on document        detail: { cart }           (fired by snippets/db-basket-fill)
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.routes || {};
  var LS_WL = 'db_wishlist';
  var LS_THEME = 'db_theme';

  /* ---------- helpers ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* Shopify money formatter that follows the store's own money format, so prices
     written by JS look exactly like the ones Liquid printed with `| money`. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    cents = parseInt(cents, 10) || 0;
    var fmt = format || window.moneyFormat || '\u20B9{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var parts = (number / 100).toFixed(precision).split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var match = fmt.match(placeholder);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value).replace(/<[^>]*>/g, '');
  }
  window.dbFormatMoney = formatMoney;

  /* Parse a fetch() response as JSON and reject with Shopify's error body on 4xx/5xx */
  function jsonOrThrow(r) {
    return r.json().then(function (data) {
      if (!r.ok) { throw data; }
      return data;
    }, function () {
      if (!r.ok) { throw { description: 'Something went wrong. Please try again.' }; }
      return {};
    });
  }

  /* ---------- toast (replaces sonner) ---------- */
  function toast(message, type) {
    var host = document.getElementById('db-toast-host');
    if (!host) { return; }
    var el = document.createElement('div');
    var color = type === 'error' ? 'border-destructive/40' : 'border-primary/30';
    el.className = 'pointer-events-auto glass-strong border ' + color + ' rounded-xl px-4 py-3 text-sm font-medium shadow-xl translate-x-4 opacity-0 transition-all duration-300';
    if (type === 'error') { el.setAttribute('role', 'alert'); }
    el.textContent = message;
    host.appendChild(el);
    requestAnimationFrame(function () { el.classList.remove('translate-x-4', 'opacity-0'); });
    setTimeout(function () {
      el.classList.add('translate-x-4', 'opacity-0');
      setTimeout(function () { el.remove(); }, 300);
    }, type === 'error' ? 4000 : 2600);
  }
  window.dbToast = toast;

  /* ============================================================
     THEME TOGGLE  (ports ThemeContext.jsx)
     ============================================================ */
  function initTheme() {
    $all('[data-theme-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', document.documentElement.classList.contains('dark') ? 'true' : 'false');
      btn.addEventListener('click', function () {
        var isDark = document.documentElement.classList.toggle('dark');
        btn.setAttribute('aria-pressed', isDark ? 'true' : 'false');
        try { localStorage.setItem(LS_THEME, isDark ? 'dark' : 'light'); } catch (e) {}
      });
    });
  }

  /* ============================================================
     MOBILE MENU + ACCOUNT DROPDOWN  (ports Navbar.jsx)
     ============================================================ */
  function initHeader() {
    var toggle = $('[data-mobile-toggle]');
    var menu = $('[data-mobile-menu]');
    var iconOpen = $('[data-mobile-icon-open]');
    var iconClose = $('[data-mobile-icon-close]');

    function setMenu(open) {
      if (!toggle || !menu) { return; }
      menu.classList.toggle('hidden', !open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (iconOpen) { iconOpen.classList.toggle('hidden', open); }
      if (iconClose) { iconClose.classList.toggle('hidden', !open); }
    }
    if (toggle && menu) {
      toggle.addEventListener('click', function () { setMenu(menu.classList.contains('hidden')); });
    }

    var acctTrigger = $('[data-account-trigger]');
    var acctDropdown = $('[data-account-dropdown]');
    function setAccount(open) {
      if (!acctTrigger || !acctDropdown) { return; }
      acctDropdown.classList.toggle('hidden', !open);
      acctTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    if (acctTrigger && acctDropdown) {
      acctTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        setAccount(acctDropdown.classList.contains('hidden'));
      });
      document.addEventListener('click', function (e) {
        if (!acctDropdown.contains(e.target) && !acctTrigger.contains(e.target)) { setAccount(false); }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') { return; }
      if (menu && !menu.classList.contains('hidden')) { setMenu(false); if (toggle) { toggle.focus(); } }
      if (acctDropdown && !acctDropdown.classList.contains('hidden')) { setAccount(false); if (acctTrigger) { acctTrigger.focus(); } }
    });
  }

  /* ============================================================
     CART  (ports CartContext.jsx onto Shopify AJAX API)
     ============================================================ */
  function updateCartCount(count) {
    $all('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
    });
    $all('[data-testid="nav-cart"]').forEach(function (a) {
      a.setAttribute('aria-label', count > 0 ? 'Basket, ' + count + (count === 1 ? ' item' : ' items') : 'Basket');
    });
  }
  window.dbUpdateCartCount = updateCartCount;

  function addToCart(id, quantity) {
    return fetch(routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity || 1 })
    }).then(jsonOrThrow);
  }

  /* The header count is kept in sync by the single cart listener in
     snippets/db-basket-fill.liquid, which fetches /cart.js once after every
     cart write and fires db:cart-updated. */
  function initCartEvents() {
    document.addEventListener('db:cart-updated', function (e) {
      if (e.detail && e.detail.cart) { updateCartCount(e.detail.cart.item_count); }
    });
  }

  /* Quick-add buttons on product cards — rendered only for single-variant products */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) { return; }
      e.preventDefault();
      e.stopPropagation();
      var id = btn.getAttribute('data-variant-id');
      if (!id || btn.getAttribute('aria-busy') === 'true') { return; }
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      addToCart(id, 1).then(function () {
        toast('Added to cart');
      }).catch(function (err) {
        toast((err && err.description) || 'Could not add to cart', 'error');
      }).finally(function () { btn.disabled = false; btn.removeAttribute('aria-busy'); });
    });
  }

  /* Product page variant selection.
     The quantity stepper and add-to-cart request live in the inline <script> at
     the bottom of sections/main-product.liquid; this function owns variant ->
     price / compare price / discount badge / availability / hidden id / URL /
     image, then fires db:variant-change so the stepper, sticky bar and delivery
     box can react. */
  function initProductForm() {
    var form = $('#product-form');
    if (!form) { return; }

    var productJson = null;
    var jsonEl = $('[data-product-json]');
    if (jsonEl) { try { productJson = JSON.parse(jsonEl.textContent); } catch (e) {} }

    function currentOptions() {
      return $all('[data-option-input]', form).filter(function (i) { return i.checked; }).map(function (i) { return i.value; });
    }
    function findVariant(opts) {
      if (!productJson) { return null; }
      return productJson.variants.find(function (variant) {
        return variant.options.every(function (o, idx) { return o === opts[idx]; });
      });
    }
    function onVariantChange() {
      var variant = findVariant(currentOptions());
      var idField = $('[data-variant-id]', form);
      var addBtn = $('[data-add-to-cart]', form);
      var addLabel = $('[data-add-label]', form);
      var priceTarget = $('[data-price-target]');
      var compareTarget = $('[data-compare-target]');
      var discountTarget = $('[data-discount-target]');
      var wishBtn = $('[data-wishlist-toggle]', form);

      if (!variant) {
        if (addBtn) { addBtn.disabled = true; }
        if (addLabel) { addLabel.textContent = 'Unavailable'; }
        if (compareTarget) { compareTarget.classList.add('hidden'); }
        if (discountTarget) { discountTarget.classList.add('hidden'); }
        form.dispatchEvent(new CustomEvent('db:variant-change', { bubbles: true, detail: { variant: null } }));
        return;
      }
      if (idField) { idField.value = variant.id; }
      if (priceTarget) { priceTarget.textContent = formatMoney(variant.price); }
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareTarget) {
        compareTarget.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compareTarget.classList.toggle('hidden', !onSale);
      }
      if (discountTarget) {
        discountTarget.textContent = onSale ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) + '% OFF' : '';
        discountTarget.classList.toggle('hidden', !onSale);
      }
      if (addBtn) { addBtn.disabled = !variant.available; }
      if (addLabel) { addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
      if (wishBtn) { wishBtn.setAttribute('data-price', formatMoney(variant.price)); }

      // Keep the variant in the URL so refresh / share / back keep the selection
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {}

      // Show the variant's own image when it has one
      var mediaId = variant.featured_media && variant.featured_media.id;
      if (mediaId) {
        var thumb = $('[data-gallery-thumb][data-media-id="' + mediaId + '"]');
        if (thumb) { thumb.click(); }
      }

      form.dispatchEvent(new CustomEvent('db:variant-change', { bubbles: true, detail: { variant: variant } }));
    }
    $all('[data-option-input]', form).forEach(function (i) { i.addEventListener('change', onVariantChange); });
  }

  /* Product gallery thumbnails (ports ProductDetail activeImg) */
  function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) { return; }
    var main = $('[data-gallery-main]', gallery);
    $all('[data-gallery-thumb]', gallery).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-src');
        var srcset = thumb.getAttribute('data-srcset');
        if (main && src) {
          if (srcset) { main.setAttribute('srcset', srcset); } else { main.removeAttribute('srcset'); }
          main.src = src;
          var alt = thumb.getAttribute('data-alt');
          if (alt) { main.alt = alt; }
        }
        $all('[data-gallery-thumb]', gallery).forEach(function (t) {
          t.classList.remove('border-primary'); t.classList.add('border-border');
          t.setAttribute('aria-current', 'false');
        });
        thumb.classList.add('border-primary'); thumb.classList.remove('border-border');
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  /* Product tabs (ports ProductDetail Tabs) */
  function initTabs() {
    var tabsRoot = $('[data-tabs]');
    if (!tabsRoot) { return; }
    var tabs = $all('[data-tab]', tabsRoot);
    function activate(btn, focus) {
      var name = btn.getAttribute('data-tab');
      tabs.forEach(function (b) {
        var active = b === btn;
        b.classList.toggle('border-primary', active);
        b.classList.toggle('text-foreground', active);
        b.classList.toggle('border-transparent', !active);
        b.classList.toggle('text-muted-foreground', !active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.setAttribute('tabindex', active ? '0' : '-1');
      });
      $all('[data-tab-panel]', tabsRoot).forEach(function (panel) {
        panel.classList.toggle('hidden', panel.getAttribute('data-tab-panel') !== name);
      });
      if (focus) { btn.focus(); }
    }
    tabs.forEach(function (btn, i) {
      btn.addEventListener('click', function () { activate(btn, false); });
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); activate(tabs[(i + 1) % tabs.length], true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); activate(tabs[(i - 1 + tabs.length) % tabs.length], true); }
      });
    });
  }

  /* ============================================================
     WISHLIST  (ports CartContext wishlist -> localStorage)
     ============================================================ */
  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(LS_WL)) || []; } catch (e) { return []; }
  }
  function writeWishlist(list) {
    try { localStorage.setItem(LS_WL, JSON.stringify(list)); } catch (e) {}
  }
  function isWishlisted(handle) {
    return readWishlist().some(function (i) { return i.handle === handle; });
  }
  function safeUrl(u) {
    // Only same-site relative paths are allowed back out of localStorage
    return (typeof u === 'string' && u.charAt(0) === '/' && u.charAt(1) !== '/') ? u : '#';
  }
  function updateWishlistCount() {
    var n = readWishlist().length;
    $all('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('hidden', n === 0);
      el.classList.toggle('flex', n > 0);
    });
  }
  function paintHearts() {
    $all('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-handle');
      var heart = btn.querySelector('.wishlist-heart');
      var on = isWishlisted(handle);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (heart) {
        if (on) { heart.classList.add('fill-primary', 'text-primary'); }
        else { heart.classList.remove('fill-primary', 'text-primary'); }
      }
    });
  }
  function initWishlistToggles() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) { return; }
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.getAttribute('data-handle');
      var list = readWishlist();
      var idx = list.findIndex(function (i) { return i.handle === handle; });
      if (idx > -1) {
        list.splice(idx, 1);
        writeWishlist(list);
        toast('Removed from wishlist');
      } else {
        list.push({
          handle: handle,
          title: btn.getAttribute('data-title'),
          url: btn.getAttribute('data-url'),
          price: btn.getAttribute('data-price'),
          image: btn.getAttribute('data-image')
        });
        writeWishlist(list);
        toast('Added to wishlist');
      }
      updateWishlistCount();
      paintHearts();
      renderWishlistPage();
    });
  }

  /* Wishlist page render (ports Wishlist.jsx) */
  function renderWishlistPage() {
    var root = $('[data-wishlist-root]');
    if (!root) { return; }
    var grid = $('[data-wishlist-grid]', root);
    var empty = $('[data-wishlist-empty]', root);
    var tpl = $('[data-wishlist-card-template]', root);
    if (!grid || !tpl) { return; }
    var list = readWishlist();
    grid.innerHTML = '';
    if (list.length === 0) {
      if (empty) { empty.classList.remove('hidden'); }
      grid.classList.add('hidden');
      return;
    }
    if (empty) { empty.classList.add('hidden'); }
    grid.classList.remove('hidden');
    list.forEach(function (item) {
      var node = tpl.content.cloneNode(true);
      var links = node.querySelectorAll('[data-card-link]');
      var img = node.querySelector('[data-card-image]');
      var title = node.querySelector('[data-card-title]');
      var price = node.querySelector('[data-card-price]');
      var remove = node.querySelector('[data-card-remove]');
      Array.prototype.forEach.call(links, function (l) { l.href = safeUrl(item.url); });
      if (img && item.image) { img.src = item.image; img.alt = item.title || ''; }
      if (title) { title.textContent = item.title || ''; }
      if (price) { price.textContent = item.price || ''; }
      if (remove) {
        remove.setAttribute('aria-label', 'Remove ' + (item.title || 'item') + ' from wishlist');
        remove.addEventListener('click', function (e) {
          e.preventDefault();
          var l = readWishlist().filter(function (i) { return i.handle !== item.handle; });
          writeWishlist(l);
          updateWishlistCount();
          renderWishlistPage();
          paintHearts();
          toast('Removed from wishlist');
        });
      }
      grid.appendChild(node);
    });
  }

  /* ============================================================
     PRODUCT RECOMMENDATIONS (Section Rendering API)
     sections/product-recommendations.liquid — its own small section, so the
     request no longer re-renders the whole product section.
     ============================================================ */
  function initRecommendations() {
    $all('[data-recommendations]').forEach(function (el) {
      var url = el.getAttribute('data-url');
      if (!url) { return; }
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-recommendations]');
        if (inner && inner.innerHTML.trim()) {
          el.innerHTML = inner.innerHTML;
          el.classList.remove('hidden');
          paintHearts();
          revealObserve();
        }
      }).catch(function () {});
    });
  }

  /* ============================================================
     REVEAL ON SCROLL (ports framer-motion whileInView)
     ============================================================ */
  var revealObserver = null;
  function revealObserve() {
    if (!('IntersectionObserver' in window)) {
      $all('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    }
    $all('.reveal:not(.is-visible)').forEach(function (el) { revealObserver.observe(el); });
    // header reveals immediately
    $all('.reveal-header').forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ---------- init ---------- */
  function init() {
    var steps = [initTheme, initHeader, initCartEvents, initQuickAdd, initProductForm, initGallery, initTabs,
      initWishlistToggles, updateWishlistCount, paintHearts, renderWishlistPage,
      initRecommendations, revealObserve];
    // One failing feature must never stop the rest (or leave content hidden)
    steps.forEach(function (fn) {
      try { fn(); } catch (err) { if (window.console) { console.error('[Deal Basket]', err); } }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
